import { useLastPageView } from "@/core/modules/tracking/use-last-page-view";
import { useCreateOrder } from "../../api/create-order.mutation";
import { useLogin as useLoginQuery } from "../../api/login.mutation";
import type { CodeValidationFormValues } from "../../components";
import { useCartStore } from "../../store/use-cart-store";
import { useOrderingStore } from "../../store/use-ordering-store";
import {
  safeTrackEvent,
  signUpNewsletterEvent,
  verificationErrorEvent,
  verificationSuccessEvent,
} from "../../tracking/events";
import { formatDateOfBirthForApi } from "../../utils/format-date-of-birth-for-api";
import { writeGaUserType } from "../../utils/ga-user-type";
import { useHandleCreateOrderErrors } from "../use-handle-create-order-errors";
import { useRoutes } from "../use-routes";
import { extractLoginErrorType, handleLoginApiErrors } from "./handle-login-errors";

export const useLogin = () => {
  const { personalDetails, timeSlot, agreements, verificationTokenUuid } = useOrderingStore();
  const { mutate: login, error: loginError, isLoading: isLoginLoading } = useLoginQuery({});
  const { products } = useCartStore();
  const { mutate: createOrder, error: createOrderError, isLoading: isCreateOrderLoading } = useCreateOrder({});
  const formattedCreateOrderError = useHandleCreateOrderErrors(createOrderError);
  const { goToHomePage, goToSummary } = useRoutes();
  const { getLastPageView, setLastPageView } = useLastPageView();
  const isLoading = isLoginLoading || isCreateOrderLoading;

  const handleLogin = (data: CodeValidationFormValues) => {
    if (!personalDetails || !verificationTokenUuid || !agreements) {
      console.error("No personal details or verification token or agreements");
      return;
    }

    const dataToSend = {
      verificationCode: data.verificationCode,
      verificationTokenUuid,
      phone: personalDetails.phone,
      dialCode: personalDetails.dialCode,
      ...agreements,
    };

    login(dataToSend, {
      onSuccess: (response) => {
        if (!agreements) {
          console.error("No agreements");
          return goToHomePage();
        }

        // REV-145: backend returns the patient uuid (or null for restricted_app_access). Propagate
        // it to lastPageView so subsequent page_view events carry user_id, and emit it on the
        // account-based verification_success.
        const patientUuid = response.patient?.uuid ?? null;
        // REV-211: propagate the patient's visits count to user_ordered_visits (mirrors user_id).
        // ?? 0 — the field is a non-null number; backend sends null for restricted_app_access.
        const userOrderedVisits = response.patient?.visitsCount ?? 0;
        const lastPageView = getLastPageView();
        if (lastPageView) {
          setLastPageView({ ...lastPageView, user_id: patientUuid, user_ordered_visits: userOrderedVisits });
        }

        safeTrackEvent(
          () => verificationSuccessEvent({ userId: patientUuid, method: "account" }),
          "verification_success",
          "login.onSuccess",
        );
        writeGaUserType("existing");

        // REV-108: existing accounts can still tick the newsletter agreement during this checkout —
        // mirror the registration flow and fire sign_up_newsletter when they do.
        if (agreements.acceptNewsletter) {
          safeTrackEvent(() => signUpNewsletterEvent(), "sign_up_newsletter", "login.onSuccess newsletter");
        }

        const { dateOfBirth, ...restPersonalDetails } = personalDetails;

        createOrder(
          {
            bloodTestPackageUuids: products.map((product) => product.uuid),
            time: timeSlot ? timeSlot.toISOString() : undefined,
            dateOfBirth: formatDateOfBirthForApi(dateOfBirth),
            ...restPersonalDetails,
            ...agreements,
          },
          {
            onSuccess: (response) => {
              return goToSummary(response.order.uuid);
            },
          },
        );
      },
      onError: (error) => {
        const errorType = extractLoginErrorType(error);
        safeTrackEvent(
          () => verificationErrorEvent({ errorType, method: "account" }),
          "verification_error",
          "login.onError",
        );
      },
    });
  };

  return {
    handleLogin,
    isLoading,
    loginError: handleLoginApiErrors(loginError),
    createOrderError: formattedCreateOrderError,
  };
};
